import { ConfigLoader } from './config-loader';
import { inject, Injectable } from '@angular/core';
import { COMMONS_ENVIRONMENT } from '../../interface/environment/commons-environment.interface';

@Injectable()
export class EnvironmentConfigLoader extends ConfigLoader {
  private readonly env = inject(COMMONS_ENVIRONMENT);

  public override loadConfiguration(origin = '') {
    this.storeEnvironmentConfigToAppConfig();
    this.loadVersion$().subscribe((version) => {
      this.config.appVersion = this.config.appVersion || version.appVersion;
      this.config.appVersionDate = this.config.appVersionDate || version.date;
      this.isLoadedValue = true;
      this.loaderEmitter.next(this.config);
      this.initTranslation();
    });
    void origin;
  }

  // We will remove this when AppConfig are removed
  private storeEnvironmentConfigToAppConfig() {
    this.storeApiConfig();
    this.storeAppConfig();
    this.storeUiConfig();
    this.storeAuthConfig();
    this.storeLoginConfig();
    this.storePortalConfig();
    this.storeChangelogConfig();
    this.storeWebsocketConfig();
    this.storeLinksConfig();
  }

  private storeApiConfig() {
    this.config.apiEndPoint = this.env.api.apiEndPoint || 'api/';
    this.config.autoBuildApi = this.env.api.autoBuildApi;
    this.config.settingsApi = this.env.api.settingsApi || 'settings';
    this.config.dataModelMethod = this.env.api.dataModelMethod || 'options';
  }

  private storeAppConfig() {
    this.config.appName = this.env.app.appName;
    this.config.appId = this.env.app.appId;
    if (this.env.app.appVersion) {
      this.config.appVersion = this.env.app.appVersion;
    }
    if (this.env.app.appVersionDate) {
      this.config.appVersionDate = this.env.app.appVersionDate;
    }
    if (this.env.app.cordova !== undefined) {
      this.config.cordova = this.env.app.cordova;
    }
    if (this.env.app.defaultRoute) {
      this.config.defaultRoute = this.env.app.defaultRoute;
    }
    if (this.env.app.noInternetRoute) {
      this.config.noInternetRoute = this.env.app.noInternetRoute;
    }
    if (this.env.app.lang) {
      this.config.lang = this.env.app.lang;
    }
    if (this.env.app.db) {
      this.config.db = this.env.app.db;
    }
    if (this.env.app.cache !== undefined) {
      this.config.cache = this.env.app.cache;
    }
  }

  private storeUiConfig() {
    if (!this.env.ui) {
      return;
    }
    if (this.env.ui.theme !== undefined) {
      this.config.theme = this.env.ui.theme;
    }
    if (this.env.ui.appLogoPath) {
      this.config.appLogoPath = this.env.ui.appLogoPath;
    }
    if (this.env.ui.exportLogoPath) {
      this.config.exportLogoPath = this.env.ui.exportLogoPath;
    }
    if (this.env.ui.lockerBackgroundPath) {
      this.config.lockerBackgroundPath = this.env.ui.lockerBackgroundPath;
    }
    if (this.env.ui.companyLogoPath) {
      this.config.companyLogoPath = this.env.ui.companyLogoPath;
    }
    if (this.env.ui.aboutUsLink) {
      this.config.aboutUsLink = this.env.ui.aboutUsLink;
    }
    if (this.env.ui.newsletterLink) {
      this.config.newsletterLink = this.env.ui.newsletterLink;
    }
    if (this.env.ui.displayFooterLoginPage !== undefined) {
      this.config.displayFooterLoginPage = this.env.ui.displayFooterLoginPage;
    }
  }

  private storeAuthConfig() {
    if (!this.env.auth) {
      return;
    }
    if (this.env.auth.loginUrl) {
      this.config.loginConfig.loginUrl = this.env.auth.loginUrl;
    }
    if (this.env.auth.logoutUrl) {
      this.config.loginConfig.logoutUrl = this.env.auth.logoutUrl;
    }
    if (this.env.auth.initApi !== undefined) {
      this.config.loginConfig.initApi = this.env.auth.initApi;
    }
    if (this.env.auth.authorizationEndPoint) {
      this.config.loginConfig.authorizationEndPoint =
        this.env.auth.authorizationEndPoint;
    }
    if (this.env.auth.azure) {
      this.config.loginConfig.azureConnection = {
        ...this.env.auth.azure,
        logoutUri:
          this.env.auth.azure.logoutUri || `${window.location.origin}/login`,
      };
    }
    if (this.env.auth.google) {
      this.config.loginConfig.googleConnection = this.env.auth.google;
    }
    if (this.env.auth.nfc) {
      this.config.loginConfig.nfcConnection = this.env.auth.nfc;
    }
  }

  private storeLoginConfig() {
    if (!this.env.login) {
      return;
    }
    if (this.env.login.loginRoute) {
      this.config.loginConfig.loginRoute = this.env.login.loginRoute;
    }
    if (this.env.login.errorRoute) {
      this.config.loginConfig.errorRoute = this.env.login.errorRoute;
    }
    if (this.env.login.resetRequest) {
      this.config.loginConfig.resetRequest = this.env.login.resetRequest;
    }
    if (this.env.login.resetPassword) {
      this.config.loginConfig.resetPassword = this.env.login.resetPassword;
    }
    if (this.env.login.authorizationRoute) {
      this.config.loginConfig.authorizationRoute =
        this.env.login.authorizationRoute;
    }
    if (this.env.login.defaultConnection !== undefined) {
      this.config.loginConfig.defaultConnection =
        this.env.login.defaultConnection;
    }
    if (this.env.login.redirectAfterLogin !== undefined) {
      this.config.loginConfig.redirectAfterLogin =
        this.env.login.redirectAfterLogin;
    }
    if (this.env.login.redirection) {
      this.config.loginConfig.redirection = this.env.login.redirection;
    }
    if (this.env.login.showCompanyField !== undefined) {
      this.config.loginConfig.showCompanyField =
        this.env.login.showCompanyField;
    }
    if (this.env.login.showPasswordReset !== undefined) {
      this.config.loginConfig.showPasswordReset =
        this.env.login.showPasswordReset;
    }
    if (this.env.login.showSettingsButton !== undefined) {
      this.config.loginConfig.showSettingsButton =
        this.env.login.showSettingsButton;
    }
    if (this.env.login.showFooter !== undefined) {
      this.config.loginConfig.showFooter = this.env.login.showFooter;
    }
    if (this.env.login.beforeSettingsRoutes) {
      this.config.loginConfig.beforeSettingsRoutes =
        this.env.login.beforeSettingsRoutes;
    }
  }

  private storePortalConfig() {
    if (!this.env.portal) return;
    this.config.loginConfig.portalConnection = this.env.portal;
  }

  private storeChangelogConfig() {
    if (!this.env.changelog) return;
    this.config.changelog = { ...this.config.changelog, ...this.env.changelog };
  }

  private storeWebsocketConfig() {
    if (!this.env.websocket) return;
    this.config.websocketConfig = { ...this.env.websocket };
  }

  private storeLinksConfig() {
    if (!this.env.links) return;
    this.config.links = { ...this.env.links };
  }
}
